import {Activity, ActivityData} from '@docma-mm-core/activity/activity';
import {Attachment, AttachmentData} from '@docma-mm-core/attachment/attachment';
import {Group, GroupData} from '@docma-mm-core/group/group';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {Fault} from './fault';
import {FaultData} from './fault-data';
import {Scribble, ScribbleData} from '@docma-mm-core/scribble/scribble';
import { Coordinate } from '@docma-mm-core/plan/plan';
import { BpPin } from '../../bp-common/models/bp-pin';


export interface ScribbleDbEntity {
  id?: number;
  uid?: string;
  loadState?: LoadState<ScribbleData>;
  changeState?: ChangeState<ScribbleData>;
  syncState?: SyncState;
}

export interface AttachmentDbEntity {
  id?: number;
  uid?: string;
  loadState?: LoadState<AttachmentData>;
  changeState?: ChangeState<AttachmentData>;
  syncState?: SyncState;
  scribbles?: ScribbleDbEntity[];
}
export interface ActivityDbEntity {
  id?: number;
  uid?: string;
  guid?: string;
  loadState?: LoadState<ActivityData>;
  changeState?: ChangeState<ActivityData>;
  syncState?: SyncState;
  attachments?: AttachmentDbEntity[];
}
export interface FaultDbEntity  {
  uid?: string;
  id?: number;
  downloaded?: boolean;
  loadState?: LoadState<FaultData>;
  changeState?: ChangeState<FaultData>;
  syncState?: SyncState;
  activities?: ActivityDbEntity[];
  attachments?: AttachmentDbEntity[];
  pinText?: string;
  attributeId?: number;
  pin?: Coordinate;
  bpPin?: BpPin;
}
export interface GroupDbEntity {
  id?: number;
  uid?: string;
  loadState?: LoadState<GroupData>;
  changeState?: ChangeState<GroupData>;
  syncState?: SyncState;
}

export const mapScribbleToEntity = async (scribble: Scribble): Promise<ScribbleDbEntity> => {
  const entity: ScribbleDbEntity = {};
  entity.id = scribble.id;
  entity.uid = scribble.uid;

  if (scribble.changeState.status !== ChangeStatusEnum.NotChanged) {
    entity.changeState = {... scribble.changeState};
  }
  entity.loadState = {...scribble.loadState};
  entity.syncState = {...scribble.syncState};
  delete entity.syncState.changes;
  return entity;
};

export const mapAttachmentToEntity = async (fault: Fault, attachment: Attachment): Promise<AttachmentDbEntity> => {

  const entity: AttachmentDbEntity = {};
  entity.id = attachment.id;
  entity.uid = attachment.uid;
  entity.scribbles = await Promise.all(attachment.scribbles.map(sc => mapScribbleToEntity(sc)));
  if (attachment.changeState.status !== ChangeStatusEnum.NotChanged) {
    entity.changeState = {... attachment.changeState};
  }
  entity.loadState = {...attachment.loadState};
  entity.syncState = {...attachment.syncState};
  delete entity.syncState.changes;
  return entity;
};

export const mapActivityToEntity = async (fault: Fault, activitiy: Activity): Promise<ActivityDbEntity> => {
  const entity: ActivityDbEntity = {};
  entity.id = activitiy.id;
  entity.uid = activitiy.uid;
  entity.guid = activitiy.group?.uid;
  if (activitiy.changeState.status !== ChangeStatusEnum.NotChanged) {
    entity.changeState = { ...activitiy.changeState};
  }
  // if (activitiy.loadState.status !== LoadStatusEnum.Loaded) {
  //   throw new Error(`Can't save not loaded activity`);
  // }
  entity.loadState = {...activitiy.loadState};
  entity.syncState = {... activitiy.syncState};
  delete entity.syncState.changes;

  if (activitiy.attachments && activitiy.attachments.length > 0) {
    entity.attachments = await Promise.all(activitiy.attachments.map( a => mapAttachmentToEntity(fault, a) ));
  }

  return entity;
};

export const  mapFaultToEntity = async (fault: Fault): Promise<FaultDbEntity> => {
  const entity: FaultDbEntity = {};
  entity.id = fault.id;
  entity.uid = fault.uid;
  entity.downloaded = fault.downloaded;
  entity.changeState = {...fault.changeState};
  entity.loadState = {...fault.loadState};
  entity.syncState = {...fault.syncState};

  entity.pin = fault.pin;
  entity.bpPin = fault.bpPin;
  entity.pinText = fault.pinText;
  entity.attributeId = fault.attributeId;

  delete entity.syncState.changes;

  if (fault.attachments && fault.attachments.length > 0) {
    entity.attachments = await Promise.all(fault.attachments.map( a => mapAttachmentToEntity(fault, a) ));
  }

  if (fault.activities && fault.activities.length > 0) {
    entity.activities = await Promise.all(fault.activities.map( a => mapActivityToEntity(fault, a) ));
  }

  return entity;
};

export const  mapGroupToEntity = async (group: Group): Promise<GroupDbEntity> => {
  const entity: GroupDbEntity = {};
  entity.id = group.id;
  entity.uid = group.uid;
  entity.changeState = {...group.changeState};
  entity.loadState = {...group.loadState};
  entity.syncState = {...group.syncState};
  delete entity.syncState.changes;

  return entity;
};



export  const mapEntityToAttachment = async (entity: AttachmentDbEntity): Promise<Attachment> => {

  const attachment =  new Attachment();
  attachment.id = entity.id;
  attachment.uid = entity.uid;
  if (entity.loadState) {
    entity.loadState.data = new AttachmentData(entity.loadState.data);
    attachment.loadState = new LoadState(entity.loadState);
  }
  if (entity.changeState) {
    entity.changeState.data = new AttachmentData(entity.changeState.data);
    attachment.changeState = new ChangeState(entity.changeState);
  }
  if (entity.syncState) {
    attachment.syncState =  new SyncState(entity.syncState);
  }

  if (entity?.scribbles) {
    const scribbles = (await Promise.all(entity.scribbles.map(sc => mapEntityToScribble(sc))));
    attachment.addScribbles(scribbles);
  }

  return attachment;
};

export  const mapEntityToScribble = async (entity: ScribbleDbEntity): Promise<Scribble> => {

  const scribble =  new Scribble();
  scribble.id = entity.id;
  scribble.uid = entity.uid;
  if (entity.loadState) {
    entity.loadState.data = new ScribbleData(entity.loadState.data);
    scribble.loadState = new LoadState(entity.loadState);
  }
  if (entity.changeState) {
    entity.changeState.data = new ScribbleData(entity.changeState.data);
    scribble.changeState = new ChangeState(entity.changeState);
  }
  if (entity.syncState) {
    scribble.syncState =  new SyncState(entity.syncState);
  }

  return scribble;
};

export const mapEntityToActivity = async (entity: ActivityDbEntity): Promise<Activity> =>  {
  const activity = new Activity();
  activity.id = entity.id;
  activity.uid = entity.uid;
  if (activity.loadState) {
    entity.loadState.data = new ActivityData(entity.loadState.data);
    activity.loadState = new LoadState(entity.loadState);

  }
  if (entity.changeState) {
    entity.changeState.data = new ActivityData(entity.changeState.data);
    activity.changeState = new ChangeState(entity.changeState);
  }
  if (entity.syncState) {
    activity.syncState =  new SyncState(entity.syncState);
  }

  if (entity.attachments && entity.attachments.length > 0) {
    activity.attachments = await Promise.all(entity.attachments.map( a => mapEntityToAttachment(a) ));
  }

  return activity;
};

export const mapEntityToFault = async (entity: FaultDbEntity): Promise<Fault> => {
  const fault = new Fault();
  fault.id = entity.id;
  fault.uid = entity.uid;
  fault.downloaded = entity.downloaded;

  fault.pin = entity.pin;
  fault.bpPin = entity.bpPin;
  fault.pinText = entity.pinText;
  fault.attributeId = entity.attributeId;



  if (entity.loadState && entity.loadState.data ) {
    entity.loadState.data = new FaultData(entity.loadState.data);
  }
  fault.loadState = new LoadState(entity.loadState);
  if (entity.changeState) {
    entity.changeState.data = new FaultData(entity.changeState.data);
    fault.changeState = new ChangeState(entity.changeState);
  }
  if (entity.syncState) {
    fault.syncState = new SyncState(entity.syncState);
  }

  fault.attachments = [];

  if (entity.attachments && entity.attachments.length > 0) {
    (await Promise.all(entity.attachments.map( a => mapEntityToAttachment(a) )))
    .forEach(a => {
      fault.addAttachment(a);
    } );
  }

  fault.activities = [];

  if (entity.activities && entity.activities.length > 0) {
    fault.addActivities(await Promise.all(entity.activities.map( a => mapEntityToActivity(a) )));
  }

  return fault;
};





